import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, TextField, FormControl, InputLabel, Select, MenuItem, Alert,
  Typography, Stack, Collapse, IconButton, Tooltip, CircularProgress, Divider,
  Switch, FormControlLabel
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  CloudUpload as UploadIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import {
  createPriceTier, updatePriceTier, upsertPriceTierItems
} from '../../services/inventoryService';
import PriceTierBulkUpload from './PriceTierBulkUpload';

const CURRENCIES = ['USD', 'GBP', 'EUR', 'AUD', 'SGD', 'CAD', 'NZD', 'CHF', 'HKD', 'JPY'];

// DT-1453: no currency is pre-filled; the user picks the currency the retailer is invoiced in.
const blank = { tier_name: '', tier_code: '', currency: '', notes: '', is_active: 1 };

/**
 * New / Edit Price Tier dialog. Reused from PriceTierAdmin (header CRUD) and
 * CustomersAdmin (inline "+ Create new tier" from the price-tier picker).
 *
 * Props:
 *   open         — boolean
 *   onClose()    — close handler (also called after a successful save)
 *   onSaved(id)  — fired with the new/updated tier id after a successful save
 *   tier         — null = create mode; object = edit mode (header only)
 *   showUpload   — show the optional bulk-upload section (default: true in
 *                  create mode, false in edit mode — items are managed on
 *                  the main screen for existing tiers)
 */
export default function NewPriceTierDialog({ open, onClose, onSaved, tier = null, showUpload }) {
  const isEdit = !!tier;
  const [form, setForm] = useState(blank);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadRows, setUploadRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(isEdit
        ? { tier_name: tier.tier_name || '', tier_code: tier.tier_code || '',
            currency: tier.currency || '', notes: tier.notes || '',
            is_active: tier.is_active ?? 1 }
        : blank);
      setUploadRows([]);
      setUploadOpen(false);
      setError(null);
    }
  }, [open, tier, isEdit]);

  const canSave = form.tier_name.trim().length > 0 && !!form.currency && !saving;
  // A tier already priced in a currency outside the list (e.g. MYR) keeps showing it.
  const currencyOptions = form.currency && !CURRENCIES.includes(form.currency)
    ? [...CURRENCIES, form.currency] : CURRENCIES;
  const uploadAllowed = showUpload !== undefined ? showUpload : !isEdit;

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      let tierId;
      if (isEdit) {
        await updatePriceTier(tier.id, form);
        tierId = tier.id;
      } else {
        const res = await createPriceTier(form);
        // backend returns { success: true, data: { id } } via controller wrapping
        tierId = res?.data?.id ?? res?.id;
        if (!tierId) throw new Error('Tier creation returned no id');
      }
      if (uploadAllowed && uploadRows.length > 0) {
        await upsertPriceTierItems(tierId, uploadRows);
      }
      onSaved?.(tierId, { itemsUploaded: uploadAllowed ? uploadRows.length : 0 });
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? `Edit Price Tier: ${tier.tier_name}` : 'New Price Tier'}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {isEdit
            ? 'Edit the tier header. Add or change SKU prices on the tier detail screen.'
            : 'Set up a new retailer / distributor pricing tier. You can optionally pre-populate SKU prices from a CSV or Excel file.'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2}>
          <Box gridColumn="span 2">
            <TextField fullWidth required label="Tier Name"
              value={form.tier_name} autoFocus
              onChange={(e) => setForm({ ...form, tier_name: e.target.value })}
              helperText="e.g. Sephora UK, Myer AU, Adore Beauty" />
          </Box>
          <FormControl fullWidth required>
            <InputLabel>Currency</InputLabel>
            <Select value={form.currency} label="Currency"
              onChange={(e) => setForm({ ...form, currency: e.target.value })}>
              {currencyOptions.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
          <Box gridColumn="span 1">
            <TextField fullWidth label="Tier Code (optional)"
              value={form.tier_code}
              onChange={(e) => setForm({ ...form, tier_code: e.target.value })}
              helperText="Short code for invoices / reports" />
          </Box>
          <Box gridColumn="span 2">
            <TextField fullWidth label="Notes" multiline minRows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Box>
          {/* Edit-only: deactivate/reactivate the tier. Inactive tiers leave
              the browse list and all product pages; the "Show hidden tiers"
              toggle on the admin list still reaches them for reactivation. */}
          {isEdit && (
            <Box gridColumn="span 1" sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Number(form.is_active) === 1}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })}
                  />
                }
                label={<Typography variant="body2">{Number(form.is_active) === 1 ? 'Active' : 'Inactive'}</Typography>}
              />
            </Box>
          )}
        </Box>

        {uploadAllowed && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" alignItems="center" spacing={1} mb={uploadOpen ? 1 : 0}>
              <Tooltip title={uploadOpen ? 'Hide upload' : 'Show upload'}>
                <IconButton size="small" onClick={() => setUploadOpen(o => !o)}>
                  {uploadOpen ? <CollapseIcon /> : <ExpandIcon />}
                </IconButton>
              </Tooltip>
              <Button
                size="small"
                startIcon={<UploadIcon />}
                onClick={() => setUploadOpen(o => !o)}
                sx={{ textTransform: 'none' }}
              >
                {uploadRows.length > 0
                  ? `${uploadRows.length} row${uploadRows.length === 1 ? '' : 's'} ready to upload`
                  : 'Upload SKU prices from CSV / Excel'}
              </Button>
            </Stack>
            <Collapse in={uploadOpen}>
              <Box sx={{ mt: 1 }}>
                <PriceTierBulkUpload
                  onChange={setUploadRows}
                  currency={form.currency}
                  compact
                />
              </Box>
            </Collapse>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={!canSave}
        >
          {saving
            ? 'Saving…'
            : isEdit
              ? 'Save Changes'
              : uploadRows.length > 0
                ? `Create Tier + Upload ${uploadRows.length}`
                : 'Create Tier'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
